// row 109 DropZone: the PDF / DOCX upload target (*Resume upload* · *PDF and DOCX*). A real <input type="file"
// accept=".pdf,.docx"> covers the whole target, so a click, Enter or Space opens the picker and a file dropped on it
// arrives through the same input; the zone takes a blue edge while a file is over it, the focus ring on the input's
// focus, and the --text-2 edge on hover. Loading: the file's name over a progress Meter (the Meter is the progressbar); the
// `phase` keeps it one progress through the upload and the scan that follows (scanning: the same Meter, full and pulsing,
// with no value announced, since the scan has no measure; no word of its own). Error: one
// of the three causes (invalid · password-protected · unreadable) as the field's alert line, aria-invalid. The zone only
// hands the chosen file to `onFile`: it never creates a row. Blocked: dashed, aria-disabled, the picker never opens.

import { useState, type ChangeEvent, type DragEvent, type MouseEvent, type ReactElement } from "react";
import { type ControlProps } from "../core/Button";
import { Meter } from "../core/Bars";
import { Field, useFieldControl } from "../core/Field";
import { Icon } from "../core/icons";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useSvgId } from "../lib/useSvgId";
import { G1_LABELS } from "./labels";

export type DropZoneError = "invalid" | "passwordProtected" | "unreadable";

const ERROR_WORDS: Readonly<Record<DropZoneError, string>> = {
  invalid: G1_LABELS.fileInvalid,
  passwordProtected: G1_LABELS.filePasswordProtected,
  unreadable: G1_LABELS.fileUnreadable,
};

export interface DropZoneProps extends ControlProps {
  /** Receives the chosen or dropped file; the host validates, uploads and passes back progress or an error. */
  onFile?: ((file: File) => void) | undefined;
  /** The field's name (default *Resume upload*). */
  label?: string | undefined;
  /** The chosen file's name (data). */
  fileName?: string | undefined;
  /** Upload progress 0–100: the loading state. */
  progress?: number | undefined;
  /** Which step of the one progress: `uploading` (default when `progress` is given) or `scanning` (indeterminate). */
  phase?: "uploading" | "scanning" | undefined;
  /** Why the file was refused. */
  error?: DropZoneError | undefined;
  required?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

function Target({ onFile, fileName, progress, phase, blocked, hs, label }: {
  onFile: DropZoneProps["onFile"];
  fileName: string | undefined;
  progress: number | undefined;
  phase: DropZoneProps["phase"];
  blocked: ControlProps["blocked"];
  hs: Hs | undefined;
  label: string;
}) {
  const hot = useHs(hs);
  const kindId = `${useSvgId()}-kind`;
  const control = useFieldControl({ blocked, "aria-describedby": kindId });
  const [over, setOver] = useState(false);
  const scanning = phase === "scanning";
  const loading = progress !== undefined || scanning;
  const inert = Boolean(control.blocked) || loading;
  const pick = (file: File | undefined) => { if (file && !inert) onFile?.(file); };
  const zone: ReactElement = (
    <span
      className={cx("mc-drop", over && "mc-drop-over", control.invalid && "mc-drop-err", control.blocked && "mc-dis", loading && "mc-drop-busy")}
      onDragEnter={() => { if (!inert) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={() => setOver(false)}
    >
      <input
        {...control.aria}
        {...hot}
        id={control.id}
        type="file"
        accept=".pdf,.docx"
        className="mc-drop-in"
        aria-busy={loading ? true : undefined}
        onClick={(event: MouseEvent<HTMLInputElement>) => { if (inert) event.preventDefault(); }}
        onDrop={(event: DragEvent<HTMLInputElement>) => { if (inert) event.preventDefault(); }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => { pick(event.target.files?.[0]); event.target.value = ""; }}
      />
      <span className="mc-drop-ic" aria-hidden="true"><Icon name="upload" size={20} /></span>
      <span className="mc-drop-tx">
        {fileName ? <b className="mc-drop-f">{fileName}</b> : null}
        <span id={kindId}>{G1_LABELS.pdfAndDocx}</span>
      </span>
    </span>
  );
  return (
    <>
      {control.blocked ? <Tooltip label={control.blocked.reason} mode="mirror">{zone}</Tooltip> : zone}
      {control.reason}
      {loading ? (
        <div className="mc-drop-prog">
          {scanning
            ? <Meter label={fileName ?? label} pct={100} value="" status progress={{ label, indeterminate: true }} />
            : <Meter label={fileName ?? label} pct={progress ?? 0} status progress={{ label }} />}
        </div>
      ) : null}
    </>
  );
}

export function DropZone({ onFile, label = G1_LABELS.resumeUpload, fileName, progress, phase, error, required, id, can, blocked, mode, hs, className }: DropZoneProps) {
  if (can === false) return null;
  return (
    <Field
      label={label}
      required={required}
      error={error ? ERROR_WORDS[error] : undefined}
      blocked={blocked}
      mode={mode}
      id={id}
      className={cx("mc-dropzone", className)}
    >
      {mode === "view" ? <span className="mc-field-v">{fileName ?? "—"}</span> : (
        <Target onFile={onFile} fileName={fileName} progress={progress} phase={phase} blocked={blocked} hs={hs} label={label} />
      )}
    </Field>
  );
}
